<template>
  <section id="principles" class="py-5 dark-section">
    <div class="container text-center">
      <h2 class="mb-5">Unsere Prinzipien</h2>
      <div class="row justify-content-center">
        <div
            class="card h-card w-90 mb-4 slide-in-card"
            :class="{ 'slide-in-card-active': cardsVisible }"
            v-for="principle in principles"
            :key="principle.id"
            @click="!isDesktop && toggleExpand(principle.id)"
        >
          <button
              class="expand-btn"
              v-if="!isDesktop"
              @click.stop="toggleExpand(principle.id)"
              aria-label="Expand principle description"
          >
            <i
                :class="expandedIds.has(principle.id)
                ? 'fas fa-chevron-up'
                : 'fas fa-chevron-down'"
            ></i>
          </button>

          <div class="card-body d-flex align-items-start">
            <div class="card-title">
              {{ principle.name }}
            </div>
            <div class="card-text">
              {{ expandedIds.has(principle.id) ? principle.description : principle.short }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onBeforeUnmount } from "vue";

export default defineComponent({
  setup() {
    const principles = ref([
      {
        id: 1,
        name: "Keine Solo-Entwicklung",
        short: "Wir arbeiten nie alleine – Ihr Feedback und Input sind uns besonders wichtig.",
        description:
            "Wir arbeiten grundsätzlich nie alleine. Ihr Feedback und Input sind uns besonders wichtig. Daher planen wir in regelmäßigen Abständen Abstimmungstreffen ein. Als Kontrolle für Sie und als wichtige Informationsquelle für uns. Natürlich sind wir auch außerhalb der regelmäßigen Treffen für Sie jederzeit erreichbar.",
        icon: "fas fa-people-arrows",
      },
      {
        id: 2,
        name: "Flexible Projektlaufzeit",
        short: "Wir passen unsere Projektlaufzeiten individuell an Ihre zeitliche Planung an.",
        description:
            "Es muss nicht immer alles morgen sein. Wir wissen das und passen unsere Projektlaufzeiten individuell an Ihre zeitliche Planung an. Von einigen Tagen bis hin zu mehreren Monaten - Sie bestimmen den Projekt-Zeitraum.",
        icon: "fas fa-calendar",
      },
      {
        id: 3,
        name: "Modulare Projektstruktur",
        short: "Unsere Projekte sind flexibel und modular aufgebaut – Stück für Stück.",
        description:
            "Manchmal wird erst im Laufe eines Projektes klar, was wirklich gebraucht wird. Daher legen wir großen Wert auf Flexibilität. Unsere Projekte sind nach Möglichkeit modular aufgebaut. Sie bestimmen genau was wir für Sie entwickeln - Stück für Stück. Sollte das Projekt einmal nicht so laufen wie geplant oder die vorgesehene Finanzierung nicht mehr da sein können Sie nach Abschluss jedes Moduls das Projekt pausieren oder abschließen!",
        icon: "fas fa-puzzle-piece",
      },
    ]);

    const expandedIds = ref(new Set<number>());
    const cardsVisible = ref(false);
    const isDesktop = ref(window.innerWidth >= 768);

    const setDesktopExpanded = () => {
      if (window.innerWidth >= 768) {
        isDesktop.value = true;
        // Expand all cards on desktop
        expandedIds.value = new Set(principles.value.map((p) => p.id));
      } else {
        isDesktop.value = false;
        // Collapse all cards on mobile by default
        expandedIds.value = new Set();
      }
    };

    const toggleExpand = (id: number) => {
      if (expandedIds.value.has(id)) {
        expandedIds.value.delete(id);
      } else {
        expandedIds.value.add(id);
      }
    };

    onMounted(() => {
      setDesktopExpanded();

      window.addEventListener("resize", setDesktopExpanded);

      const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                cardsVisible.value = true;
                observer.disconnect();
              }
            });
          },
          { threshold: 0.1 }
      );

      const principlesSection = document.querySelector("#principles .row");
      if (principlesSection) observer.observe(principlesSection);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", setDesktopExpanded);
    });

    return { principles, expandedIds, toggleExpand, cardsVisible, isDesktop };
  },
});
</script>

<style scoped lang="less">
@import "@/assets/style.less";
@import "@/assets/animations.less";

</style>
